import { usePiiLookup } from "@/api/pii-vault/use-pii-lookup";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Loader2, Search } from "lucide-react";
import { type FormEvent, useState } from "react";
import { PiiBackfillCard } from "./components/pii-backfill-card";
import { PiiOrdersParcelsTable } from "./components/pii-orders-parcels-table";
import { PiiReturnRequestsTable } from "./components/pii-return-requests-table";

/**
 * Super-admin debugging screen of the PII vault (RGPD-PII-VAULT-PLAN.md, step
 * 1 bis), shown by Returns and by the super admin. The caller checks the
 * superpower. Copy is hardcoded in French like the other Baback-internal pages.
 */
export function PiiLookupView({
  shopId,
  shopName,
}: {
  shopId: string;
  shopName: string;
}) {
  const [email, setEmail] = useState("");
  const lookup = usePiiLookup(shopId);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    lookup.mutate(email.trim());
  };
  const result = lookup.data;
  const returnRequests =
    result?.documents.filter((d) => d.kind === "returnRequest") ?? [];
  const ordersAndParcels =
    result?.documents.filter((d) => d.kind !== "returnRequest") ?? [];

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Compare ce que les commandes, retours et colis de {shopName} contiennent
        en clair avec ce que le coffre rend pour chacun. Recherche sur l'email
        exact.
      </p>

      <PiiBackfillCard shopId={shopId} />

      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          required
          placeholder="client@exemple.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="max-w-md"
        />
        <Button type="submit" disabled={lookup.isPending}>
          {lookup.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          Rechercher
        </Button>
      </form>

      {result && (
        <div className="space-y-4" data-dd-privacy="mask">
          {!result.isVaultAvailable && (
            <p className="text-sm text-amber-700">
              Le coffre est désactivé ou indisponible sur cet environnement :
              seules les données en clair sont affichées.
            </p>
          )}
          <div>
            <p className="font-medium mb-1">
              Retours ({returnRequests.length})
            </p>
            <PiiReturnRequestsTable
              shopId={shopId}
              documents={returnRequests}
            />
          </div>

          <div>
            <p className="font-medium mb-1">
              Commandes et colis ({ordersAndParcels.length})
            </p>
            <PiiOrdersParcelsTable documents={ordersAndParcels} />
          </div>

          <p className="text-xs text-muted-foreground">
            {result.documentLimitPerKind} document(s) au plus par type.
          </p>
        </div>
      )}
    </div>
  );
}
